import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid2 as Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import copy from "fast-copy";
import { useSnackbar } from "notistack";
import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Assign, DateTimePicker, DeletePanel, HighlightSelectBox, MyPaper, SectionPaper, TagsHighlightSelect, TextField, UnAssign } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { AssignmentStatus, useCreateQuoteMutation, useGetCurrencyQuery, useLazyGetUsersQuery, useUpdateAssignmentMutation, useUpdateQuoteMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { QuoteSliceInterface, RevisionInfo, updateQuoteSlice } from "../../../redux/slices/quote";
import { HasAccess } from "../../../router/authorization";
import { PERMISSIONS } from "../../../router/permission";
import { AppRoutes } from "../../../router/routes";
import { ConvertToReactSelect, getUserSelectedCurrency, sortByKey, TAG_CANONICAL_NAMES } from "../../../utils";
import { mandateKeys, preparePayload } from "../utils";
import { PublishedIcon } from "../../../images/svg/publishedIcon";
import { WIPIcon } from "../../../images/svg/WIPIcon";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { PublishComponent } from "../../materials/dashboard";
import moment from "moment";
import { QuoteRevisionTable } from "./revisionTable";

export const BasicDetails: React.FC<{ children?: JSX.Element, quoteId: string, isEdit: boolean, refetch: any, readOnly: boolean, setIsSaved: any }> = ({ readOnly, ...props }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location: any = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const quote = useAppSelector(store => store.quote);
    const { userDetails, userPreferences } = useAppSelector(store => store.auth);
    const { perm } = userDetails;

    const DEFAULT_CURRENCY = getUserSelectedCurrency(userPreferences).value;

    const currencyData = useGetCurrencyQuery(null);
    const [getUsers, usersResponse] = useLazyGetUsersQuery();

    const [createQuote, createQuoteData] = useCreateQuoteMutation();
    const [updateQuote, updateQuoteData] = useUpdateQuoteMutation();
    const [assignSequenceNumberQuery] = useUpdateAssignmentMutation();

    const userOptions = React.useMemo(() => {
        if (usersResponse.isSuccess) {
            return ConvertToReactSelect(usersResponse?.data?.data || [], "user_id", "name");
        }
        return [];
        // eslint-disable-next-line
    }, [usersResponse.isFetching, usersResponse.isLoading])

    // const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_CREATE), [perm]);
    // const editable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_UPDATE), [perm]);
    const publishable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_PUBLISH), [perm]);

    const [state, setState] = React.useState<QuoteSliceInterface>(quote);

    const updateBasicDetails = (key: keyof QuoteSliceInterface, value: any) => {
        setState({ ...state, [key]: value });

        if (props.isEdit) {
            props.setIsSaved(false);
        }
    };

    const isValid = () => {
        let isValid = true;
        let newState: any = copy(state);
        for (let index = 0; index < mandateKeys.length; index++) {
            const value: any = copy(newState)[mandateKeys[index]];
            if (!value) {
                isValid = false;
                enqueueSnackbar('Please fill mandatory fields (*) correctly.', { variant: "warning" });
                break;
            }

            if (typeof value === "object") {
                const revVal = (value as RevisionInfo);
                if (
                    ((revVal.approved_by?.include && !revVal.approved_by?.value) ||
                        (revVal.created_by?.include && !revVal.created_by?.value) ||
                        (revVal.referred_by?.include && !revVal.referred_by?.value))
                ) {
                    isValid = false;
                    enqueueSnackbar('Please fill mandatory fields (*) correctly.', { variant: "warning" });
                    break;
                }
            }
        }

        return isValid;
    };

    const generateSerialNumbersForTemplate = async (id: string | null, serial_num: string, type: AssignmentStatus = "assign", isManual?: boolean) => {
        dispatch(openBackdrop(`${type === "assign" ? "Assigning" : "Un Assigning"} Serial Number....`));
        let object_type = `quote`;
        let object_id = props.quoteId;

        await assignSequenceNumberQuery({
            id: id,
            status: type,
            manual_serial_num: isManual,
            payload: [
                {
                    object_type: object_type,
                    object_id: object_id,
                    serial_num: (type === "unassign" || isManual || serial_num) ? serial_num : undefined,
                },
            ],
        }).then(async (res: any) => {
            if (res.error) {
                enqueueSnackbar(`Something went wrong unable to ${type === 'assign' ? "assign" : "un-assign"} serial number. Info: ${res?.error?.data?.internal_error ?? res?.error?.data?.title ?? "-"}`, { variant: "error" })
            } else {
                enqueueSnackbar(`${type === 'assign' ? "Assigned" : "Un Assigned"} Serial Number`, { variant: "success" });
                dispatch(closeDialog());
                props.refetch();
            }
        }).catch((err: any) => {
            console.error('err:', err)
            enqueueSnackbar(err?.data?.internal_error ?? err?.data?.title ?? 'Something went wrong unable to assign serial number', { variant: "error" });
        });
        dispatch(closeBackdrop());
    };

    const onAssignClick = () => {
        dispatch(
            openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                enablePadding: false,
                body: <Assign onAssign={(id?: string | null, serial_num?: string, isManual?: boolean, type: AssignmentStatus = "assign") => generateSerialNumbersForTemplate(id ?? "", serial_num ?? "", type, isManual)} />,
            })
        );
    };

    const onUnAssignClick = () => {
        dispatch(
            openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                body: (
                    <UnAssign
                        onCancel={() => dispatch(closeDialog())}
                        onUnassign={() => generateSerialNumbersForTemplate(null, quote?.serial_num ?? "", "unassign")}
                    />
                ),
            })
        );
    };

    const onCreateUpdateQuoteDetails = async (updateVersion: boolean = false) => {
        const payload = { ...preparePayload(state) };

        try {
            if (props.isEdit) {
                dispatch(openBackdrop('Updating Quote....'));
                await updateQuote({ id: props.quoteId, payload, update_version: updateVersion }).then((res: any) => {
                    if (res.data) {
                        props.setIsSaved(true);
                        enqueueSnackbar('Quote Updated Successfully!', { variant: "success" });
                        let newQuoteId = res.data?._id ?? "New";
                        navigate(AppRoutes.viewQuickQuoteBasicDetail(newQuoteId), { state: location.state });
                    } else {
                        props.refetch();
                        console.log('error:', res.error)
                        enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
                    }
                });
            } else {
                dispatch(openBackdrop('Creating Quote....'));
                await createQuote(payload).then((res: any) => {
                    if (res.data) {
                        props.setIsSaved(true);
                        enqueueSnackbar('Quote Created Successfully!', { variant: "success" });
                        let newQuoteId = res.data?._id ?? "New";
                        navigate(AppRoutes.viewQuickQuoteBasicDetail(newQuoteId), { state: location.state });
                    } else {
                        props.refetch();
                        console.log('error:', res.error)
                        enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
                    }
                });
            }
            dispatch(closeBackdrop())
            dispatch(closeConfirmationDialog())
            dispatch(updateQuoteSlice(state))
        } catch (error) {
            console.log('error:', error)
        }

        setSearchParams({});
    };

    const createUpdateQuoteDetails = async () => {
        if (!isValid()) {
            return false;
        }

        if (props.isEdit && quote.released) {
            dispatch(openConfirmationDialog({
                title: "Update Operation",
                body: `Current revision for ${quote.sequence_id}-r${quote.version} is already published. Do you want to create a new revision?`,
                positiveBtn: "Yes",
                negativeBtn: "Cancel",
                onOk: () => onCreateUpdateQuoteDetails(true),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            }));
        } else if (props.isEdit) {
            dispatch(openConfirmationDialog({
                title: "Update Operation",
                body: <DeletePanel
                    isMaterial={true}
                    message={`Are you sure want to overwrite?`}
                    warningMessage={`The dependent items of ${quote.sequence_id}-r${quote.version} will be updated with new changes`}
                />,
                positiveBtn: "Overwrite",
                negativeBtn: "Cancel",
                onOk: () => onCreateUpdateQuoteDetails(false),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            }));
        } else {
            onCreateUpdateQuoteDetails(false);
        }
    };

    const giveMeErrorMessage = () => {
        let message = `Oops! Something went wrong, Unable to ${props.isEdit ? "Update" : "Create"} Quote. Try Again Later!`;
        let errorData: any = props.isEdit ? updateQuoteData.error : createQuoteData.error;
        message = errorData?.data?.internal_error ?? errorData?.data?.title ?? message;
        return message;
    };

    const updatePublishStatus = () => {

        if (!quote.quote_report?.processing && !quote.quote_report?.attachment_id) {
            dispatch(openConfirmationDialog({ title: "Publish Operation", body: "Please generate the quote report before publishing.", positiveBtn: "Ok", hideNegativeBtn: true, onOk: () => dispatch(closeConfirmationDialog()) }));
            return;
        }

        if (quote.quote_report?.processing) {
            dispatch(openConfirmationDialog({ title: "Publish Operation", body: "Quote report is being generated. Please wait for a moment.", positiveBtn: "Ok", hideNegativeBtn: true, onOk: () => dispatch(closeConfirmationDialog()) }));
            return;
        }

        dispatch(
            openConfirmationDialog({
                title: "Publish Operation",
                body: <DeletePanel
                    isMaterial={true}
                    message={`Are you sure you want to publish the quote ${quote?.sequence_id}?`}
                    warningMessage={`You will not be able to edit this revision after publishing. You can however create new revisions.`}
                />,
                positiveBtn: "Yes",
                negativeBtn: "Cancel",
                onOk: () => proceedToPublish(),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            })
        );
    };

    const proceedToPublish = () => {
        dispatch(closeConfirmationDialog());
        dispatch(openDialog({
            title: "",
            body: <PublishComponent {...quote} _id={quote.id} refetch={props.refetch} object_type="quote" object_id={props.quoteId} />,
            hidePositiveBtn: true,
            hideNegativeBtn: true,
            enablePadding: false
        }));
    };


    React.useEffect(() => {
        if (!updateQuoteData.isLoading && updateQuoteData.isError) {
            enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
        }
        // eslint-disable-next-line
    }, [updateQuoteData.isError]);

    React.useEffect(() => {
        let currencyObj = {};
        if (!props.isEdit) {
            currencyObj = { currency: { label: DEFAULT_CURRENCY, value: DEFAULT_CURRENCY } }
        }
        setState({ ...quote, ...currencyObj });
        // eslint-disable-next-line
    }, [quote, DEFAULT_CURRENCY]);

    React.useEffect(() => {
        getUsers({});
        props.setIsSaved(true);
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (Boolean(searchParams.get("forceSave"))) {
            createUpdateQuoteDetails();
        };
        // eslint-disable-next-line
    }, [searchParams]);

    const isLoading = createQuoteData.isLoading || updateQuoteData.isLoading;

    return (
        (<Stack spacing={1} sx={{
            mb: 2
        }}>
            {/* Basic Data Section */}
            <SectionPaper stack title="Basic Details" renderButtons={[
                <>
                    {props.isEdit && <>
                        <Stack direction={"row"} sx={{
                            alignItems: "center"
                        }}>
                            <Typography variant="body2" color="textSecondary">Status: </Typography>

                            <Tooltip title={quote.released ? "Published" : publishable ? "Work in progress, click to publish the item" : "Not Published"} arrow placement="top" >
                                <Box>
                                    {quote.released && <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                        <PublishedIcon sx={{ width: "100%", height: "100%" }} />
                                    </Box>}

                                    {!quote.released && <Box
                                        sx={{
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                        <IconButton sx={{ width: "30px", height: "30px", padding: 0 }} onClick={() => publishable ? updatePublishStatus() : false}>
                                            <WIPIcon sx={{ width: "100%", height: "100%" }} />
                                        </IconButton>
                                    </Box>}
                                </Box>
                            </Tooltip>
                        </Stack>
                    </>}
                </>
            ]}>
                <Grid container spacing={2}>
                    {/* Name */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TextField
                            id="iequote-name"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={state.name}
                            label={"Name"}
                            onChange={(e) => updateBasicDetails("name", e.target.value)}
                            required={true}
                            viewOnly={readOnly || quote?.released}
                        />
                    </Grid>

                    {/* Status */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TagsHighlightSelect
                            id="iequote-status"
                            margin={"none"}
                            value={state.status ? state.status : null}
                            multiple={false}
                            onChange={(data: SelectBoxOption) => updateBasicDetails("status", data)}
                            viewOnly={readOnly || quote?.released}
                            canonical_name={TAG_CANONICAL_NAMES.quote_status}
                        />
                    </Grid>

                    {/* Priority */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TagsHighlightSelect
                            id="iequote-priority"
                            margin={"none"}
                            value={state.priority ? state.priority : null}
                            multiple={false}
                            onChange={(data: SelectBoxOption) => updateBasicDetails("priority", data)}
                            viewOnly={readOnly || quote?.released}
                            canonical_name={TAG_CANONICAL_NAMES.quote_priority}
                        />
                    </Grid>

                    {/* Currency */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <HighlightSelectBox
                            id="iequote-currency"
                            label={"Currency"}
                            margin={"none"}
                            required={true}
                            value={state.currency ? state.currency : null}
                            options={
                                sortByKey(currencyData?.data || [], "currency_code") ?? []
                            }
                            loading={currencyData.isLoading}
                            multiple={false}
                            onChange={(data: SelectBoxOption) => updateBasicDetails("currency", data)}
                            viewOnly={readOnly || quote?.released}
                        />
                    </Grid>

                    {/* Tags */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <TagsHighlightSelect
                            id="iequote-tags"
                            margin={"none"}
                            value={state.tags ? state.tags : null}
                            multiple={true}
                            onChange={(data: SelectBoxOption) => updateBasicDetails("tags", data)}
                            viewOnly={readOnly || quote?.released}
                            canonical_name={TAG_CANONICAL_NAMES.quote_tg}
                        />
                    </Grid>

                    {/* Custom Quote */}
                    <Grid size={{
                        xs: 12,
                        sm: 12,
                        md: 6
                    }}>
                        <FormControlLabel
                            id={`${props.quoteId}-checkbox-label`}
                            key={`${props.quoteId}-checkbox-label-key`}
                            control={<Checkbox
                                id={`${props.quoteId}-checkbox`}
                                key={`${props.quoteId}-checkbox-key`}
                                checked={state.customQuote}
                                onChange={(e) => updateBasicDetails("customQuote", e.target.checked)}
                            />}
                            label={'Custom Quote'}
                            disabled={readOnly || quote?.released}
                        />
                    </Grid>

                    {/* Serial Number For Quote */}
                    {props.isEdit && (
                        <Grid size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <Stack sx={{ overflowWrap: "break-word" }}>
                                    <Typography variant="body1" sx={{ fontFamily: "htrts_medium" }}>Custom Reference No.</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "htrts_regular" }}>{typeof quote.serial_num === "string" ? quote.serial_num : "N/A"}</Typography>
                                </Stack>
                                {(!readOnly && !quote?.released) &&
                                    <Stack direction={"row"} spacing={2}>
                                        <Button variant={"contained"} size="small" onClick={() => typeof quote.serial_num === "string" ? onUnAssignClick() : onAssignClick()}>
                                            {typeof quote.serial_num === "string" ? "Unassign" : "Assign"}
                                        </Button>
                                    </Stack>
                                }
                            </Stack>
                        </Grid>
                    )}

                    {/* Divider */}
                    <Grid size={12}>
                        <Divider variant="fullWidth" />
                    </Grid>

                    {/* Creation Date */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <DateTimePicker
                            value={state.creationDate ? state.creationDate : null}
                            onChange={(current_date) => {
                                setState({
                                    ...state,
                                    creationDate: current_date || undefined,
                                    validTill: moment(current_date).add(1, 'M')
                                });
                            }}
                            label="Creation Date *"
                            // format="DD-MM-YYYY"
                            formatDensity="spacious"
                            sx={{ width: "100%" }}
                            viewOnly={readOnly || quote?.released}
                        />
                    </Grid>

                    {/* Valid Till */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <DateTimePicker
                            value={state.validTill ? state.validTill : null}
                            onChange={(current_date) => updateBasicDetails("validTill", current_date)}
                            label="Valid Till *"
                            // format="DD-MM-YYYY"
                            formatDensity="spacious"
                            sx={{ width: "100%" }}
                            viewOnly={readOnly || quote?.released}
                        />
                    </Grid>
                </Grid>
            </SectionPaper>

            <QuoteRevisionTable
                userOptions={userOptions}
                revision_info={state.revision_info}
                onItemChanged={(key, value) => updateBasicDetails("revision_info", { ...state.revision_info, [key]: value })}
                readOnly={readOnly || state.released}
            />

            {/* Alert Box */}
            {(createQuoteData.isError || updateQuoteData.isError) && <Alert sx={{ mt: 2 }} severity="error">{giveMeErrorMessage()}</Alert>}

            {/* Footer Button */}
            {
                !readOnly && <MyPaper padding={0}>
                    <Box sx={{ paddingX: 2 }}>
                        <Stack
                            direction={{ sm: "column", md: "row" }}
                            gap={1}
                            sx={{
                                alignItems: "center",
                                justifyContent: "center",
                                p: 2
                            }}>

                            {/* Clear All Button */}
                            <Button disabled={isLoading} variant="outlined" sx={{ width: 200 }} color="primary" onClick={() => setState({
                                ...state,
                                name: "",
                                status: null,
                                currency: null,
                                tags: [],
                                creationDate: undefined,
                                validTill: undefined,
                            })} >
                                Clear All
                            </Button>

                            {/* Create Button */}
                            <LoadingButton loading={isLoading} variant="contained" sx={{ width: 200 }} color="primary" onClick={createUpdateQuoteDetails}>
                                {props.isEdit ? "Update" : "Create"}
                            </LoadingButton>

                            {/* Cancel Button */}
                            <Button disabled={isLoading} variant="outlined" sx={{ width: 200 }} color="primary" onClick={() => navigate(AppRoutes.allQuickQuotes, { state: location.state })} >
                                Cancel
                            </Button>
                        </Stack>
                    </Box>
                </MyPaper>
            }
        </Stack >)
    );
}